import { FormControlLabel, Modal, Radio, RadioGroup } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";
import {
  ProfileModalWrapper,
  ProfileModalTitle,
  ProfileModalTitleWrapper,
  ProfileModalButtons,
} from "../shared/Modal/Modal.styles";
import {
  ProfileAlabamaModalInfo,
  ProfileAlabamaModalInfoText,
  ProfileAlabamaModalInfoTitle,
  ProfileAlabamaModalInfoDescription,
  ProfileAlabamaModalInfoWrapper,
  ProfileAlabamaModalWrapper,
} from "./ProfileAlabama.styles";
import { patientProfileState } from "../../recoil/atoms";
import {
  RequisitionModelSerializer,
  SupportPhoneNumber,
} from "../../recoil/types";
import { getUserAuth } from "../../recoil/selectors";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { SubmitABNInfo } from "./Services/UpdateProfileInfoService";

export const ProfileAlabamaWarning: React.FC<{
  alabamaModalOpen: boolean | undefined;
  setAlabamaModalOpen: (show: boolean) => void;
  requisition: RequisitionModelSerializer;
}> = ({ alabamaModalOpen, setAlabamaModalOpen, requisition }) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const [agreeOrCancel, setAgreeOrCancel] = useState("agree");
  const auth = useRecoilValue(getUserAuth);
  const [patientInfo] = useRecoilState(patientProfileState);

  const [loading, setLoading] = useState(false);

  const submitABNForm = async () => {
    setLoading(true);
    await SubmitABNInfo(
      agreeOrCancel === "agree",
      requisition.req_identifier,
      auth?.access_token,
    );
    setAlabamaModalOpen(false);
    setLoading(false);
  };

  return (
    <Modal
      open={alabamaModalOpen ?? false}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          setAlabamaModalOpen(false);
        }
      }}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <ProfileAlabamaModalWrapper deviceType={deviceType}>
        <ProfileModalWrapper
          deviceType={deviceType}
          style={{ overflow: "auto" }}
        >
          <ProfileModalTitle deviceType={deviceType}>
            <ProfileModalTitleWrapper deviceType={deviceType}>
              <div>{t("profile.alabamaWarning.title")}</div>
            </ProfileModalTitleWrapper>
          </ProfileModalTitle>
          <ProfileAlabamaModalInfoWrapper>
            <ProfileAlabamaModalInfo>
              <ProfileAlabamaModalInfoText>
                {t("profile.alabamaWarning.fields.patientName")}{" "}
                <span>
                  {" "}
                  {`${patientInfo?.phi.first_name} ${patientInfo?.phi.last_name}`}
                </span>
              </ProfileAlabamaModalInfoText>
              <ProfileAlabamaModalInfoText>
                {t("profile.alabamaWarning.fields.patientDateOfBirth")}{" "}
                <span>{`${patientInfo?.phi.dob}`}</span>
              </ProfileAlabamaModalInfoText>
              <ProfileAlabamaModalInfoText>
                {t("profile.alabamaWarning.fields.healthcareProvider")}{" "}
                <span>{requisition.physician_name}</span>
              </ProfileAlabamaModalInfoText>
            </ProfileAlabamaModalInfo>
            <ProfileAlabamaModalInfoTitle>
              {t("profile.alabamaWarning.noticeTitle")}
            </ProfileAlabamaModalInfoTitle>
            <ProfileAlabamaModalInfoText>
              The services listed below have been ordered for me by my
              healthcare provider (listed above) because my healthcare provider
              believes that the services benefit my care. Medicaid may not cover
              these services. The cost for these services is <span>$349</span>{" "}
              for Medicaid patients (if either or both tests are ordered). I
              understand that I am responsible for paying for these services
              ordered by my healthcare provider.{" "}
            </ProfileAlabamaModalInfoText>
            <ProfileAlabamaModalInfoText>
              {t("profile.alabamaWarning.servicesOrdered")}
            </ProfileAlabamaModalInfoText>
            <ul>
              <li>
                <ProfileAlabamaModalInfoDescription>
                  <span>
                    {" "}
                    Unity Carrier Complete<sup>&reg;</sup> (with or without
                    Fetal Risk Assessment){" "}
                  </span>
                </ProfileAlabamaModalInfoDescription>
              </li>
              <li>
                <ProfileAlabamaModalInfoDescription>
                  <span>
                    {" "}
                    Unity Aneuploidy Complete<sup>&reg;</sup> (with or without
                    Fetal Antigen NIPT)
                  </span>
                </ProfileAlabamaModalInfoDescription>
              </li>
            </ul>
            <br />
            <br />
            <ProfileAlabamaModalInfoText>
              If you have questions about the services, please contact
              BillionToOne at {SupportPhoneNumber}. If you have other questions
              about Medicare billing, call 1-800-MEDICARE (1-800-633-4227/TTY:
              1-877-486-2048).
            </ProfileAlabamaModalInfoText>
            <br />
            <br />
            <RadioGroup
              id="alabama-patient-service"
              style={{ marginTop: "-8px" }}
              value={agreeOrCancel}
              name="service"
              onChange={(e) => setAgreeOrCancel(e.target.value)}
            >
              <FormControlLabel
                id="new-test-patient-female"
                value="agree"
                control={<Radio />}
                label={
                  <>
                    I acknowledge receipt and understanding of this notice and{" "}
                    <span style={{ fontWeight: 700 }}>
                      {" "}
                      agree that I am responsible for the cost of the services
                      referenced above. The cost may be up to $349.{" "}
                    </span>
                  </>
                }
              />
              <br />
              <FormControlLabel
                id="new-test-patient-male"
                value="cancel"
                control={<Radio />}
                label={
                  <>
                    I acknowledge receipt and understanding of this notice and
                    <span style={{ fontWeight: 700 }}>
                      {" "}
                      want to cancel the services referenced above. No payment
                      will be required.
                    </span>
                  </>
                }
              />
            </RadioGroup>
          </ProfileAlabamaModalInfoWrapper>
          {loading ? (
            <SpinnerLoading />
          ) : (
            <ProfileModalTitle deviceType={deviceType}>
              <ProfileModalButtons deviceType={deviceType}>
                <div />
                <Button
                  label={t("profile.alabamaWarning.actions.submit")}
                  onClick={() => submitABNForm()}
                  size="medium"
                />
              </ProfileModalButtons>
            </ProfileModalTitle>
          )}
        </ProfileModalWrapper>
      </ProfileAlabamaModalWrapper>
    </Modal>
  );
};
